import { createElement, Fragment, type ReactNode } from "react";
import { lerHtml, type NoRico } from "@noesis/shared";

/**
 * Renderiza o conteúdo do caderno — **sem `innerHTML`, em nenhum caminho**.
 *
 * O HTML guardado passa pelo saneador (`lerHtml`), que devolve árvore tipada, e
 * daí saem elementos React. `dangerouslySetInnerHTML` não aparece aqui, e é
 * isso — não a lista de permissão — que fecha o caminho de injeção. A lista
 * existe para outra coisa: garantir o invariante 17, que nenhum conteúdo
 * persistido carregue atributo `style`.
 *
 * É o mesmo desenho do leitor de Markdown que este substitui, pela mesma razão.
 *
 * O saneamento roda aqui **de novo**, mesmo o conteúdo já tendo sido saneado na
 * gravação (RN-41). Não é desconfiança do próprio servidor: é que o vocabulário
 * de classes pode encolher, e uma nota escrita sob o vocabulário antigo precisa
 * perder a classe que saiu dele na leitura, não continuar pintada por uma regra
 * de CSS que ainda não foi apagada.
 */
export function ConteudoRico({ html }: { html: string }) {
  return <div className="conteudo-rico leitura">{montar(lerHtml(html))}</div>;
}

const PERMITIDAS = new Set([
  "p",
  "h1",
  "h2",
  "h3",
  "ul",
  "ol",
  "li",
  "blockquote",
  "pre",
  "hr",
  "strong",
  "em",
  "u",
  "s",
  "code",
  "a",
  "span",
  "mark",
  "br",
  // A tabela da barra de ferramentas da Fase 2.
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
]);

function montar(nos: NoRico[]): ReactNode {
  return nos.map((no, i) => <Fragment key={i}>{montarNo(no)}</Fragment>);
}

function montarNo(no: NoRico): ReactNode {
  if (no.tipo === "texto") return no.valor;

  // A árvore já vem filtrada; esta guarda existe para o dia em que alguém
  // acrescentar uma tag ao saneador e esquecer de acrescentá-la aqui.
  if (!PERMITIDAS.has(no.tag)) return montar(no.filhos);

  const props: Record<string, unknown> = {};
  if (no.classes.length > 0) props.className = no.classes.join(" ");
  if (no.tag === "a" && no.href) {
    props.href = no.href;
    // Link de nota abre fora: o caderno não é navegação do app, e voltar de uma
    // aba nova não custa o estado do editor.
    props.target = "_blank";
    props.rel = "noreferrer noopener";
  }

  // `createElement` com a tag em texto, e não JSX com variável: a tag vem da
  // árvore e o TypeScript não tem como estreitar uma `string` para o conjunto
  // de elementos conhecidos. A guarda acima é o que faz esse texto ser seguro.
  if (no.tag === "br" || no.tag === "hr") return createElement(no.tag, props);
  return createElement(no.tag, props, montar(no.filhos));
}
